import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { OrgNav } from '../components/OrgNav.js';
import { ErrorBanner, Loading, Spinner } from '../components/ui.js';
import {
  api,
  type Organization,
  type Plan,
  type PlanService,
  type Subscription,
} from '../lib/api.js';
import { formatMoney, formatNumber, formatRate, pluralise } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/**
 * Plan picker.
 *
 * Subscribing to a paid plan returns a hosted checkout URL from the billing
 * provider; the browser is redirected there. The subscription does not become
 * active until the provider confirms payment by webhook, which is stated on the
 * page so the redirect is not a surprise.
 */
/** A bare capability flag carries no quantity — its unit is literally "feature". */
const isFlag = (service: PlanService): boolean => service.unit === 'feature';

const rank = (service: PlanService): number =>
  service.kind === 'metered' ? 0 : isFlag(service) ? 2 : 1;

/**
 * One line of a plan's feature list.
 *
 * Flags render as their name alone. Anything with a real unit renders as
 * "Name: quantity unit", which avoids the "10 users active users" that comes
 * from concatenating a unit and a name that share a noun.
 */
function describeGrant(service: PlanService): string {
  if (isFlag(service)) return service.name;
  if (service.includedQuantity === null) return `${service.name}: unlimited`;
  return `${service.name}: ${formatNumber(service.includedQuantity)} ${pluralise(
    service.includedQuantity,
    service.unit,
  )}`;
}

export function PlansPage(): ReactNode {
  const { orgId = '' } = useParams();
  // The published price list is INR only; the schema stays per-currency so a
  // real second currency can be added without a migration.
  const [currency] = useState('INR');

  const org = useAsync(() => api.get<Organization>(`/api/organizations/${orgId}`), [orgId]);
  const plans = useAsync(
    () => api.get<{ data: Plan[] }>(`/api/plans?currency=${currency}&interval=month`),
    [currency],
  );
  const subscription = useAsync(
    () =>
      api.get<{ subscription: Subscription | null }>(`/api/organizations/${orgId}/subscription`),
    [orgId],
  );

  const { run, pending, error } = useAction();
  const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);

  if (org.loading || plans.loading) return <Loading />;

  const current = subscription.data?.subscription ?? null;
  const canManage = org.data?.role === 'owner' || org.data?.role === 'admin';

  // A tenant on a zero-price plan can move freely: nothing has been paid, so
  // there is nothing to prorate or schedule.
  const currentPlan = (plans.data?.data ?? []).find((plan) => plan.id === current?.planId);
  const onFreePlan = current !== null && (currentPlan?.basePriceMinor ?? 0) === 0;

  async function choose(plan: Plan): Promise<void> {
    setPendingPlanId(plan.id);
    await run(async () => {
      const result = await api.post<{ subscription: Subscription; checkoutUrl: string | null }>(
        `/api/organizations/${orgId}/subscription`,
        { planId: plan.id },
      );
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
        return;
      }
      subscription.reload();
    });
    setPendingPlanId(null);
  }

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <h1>Plans</h1>
          <p>
            All prices exclude GST. Extra active users and storage are billed monthly at the plan’s
            overage rate.
          </p>
        </div>
      </div>

      <OrgNav orgId={orgId} />

      <div className="stack">
        <ErrorBanner error={plans.error} />
        <ErrorBanner message={error} />

        {current && current.status !== 'canceled' && !onFreePlan && (
          <div className="banner banner-warning">
            Changing between paid plans is not supported yet. Cancel the current subscription first
            — it stays active until the end of the period you have paid for.
          </div>
        )}

        <div className="grid">
          {(plans.data?.data ?? []).map((plan) => {
            const isCurrent = current?.planId === plan.id;
            // Metered allowances first — they are what the price actually buys —
            // then ceilings, then plain capability flags.
            const granted = plan.services
              .filter((s) => s.enabled && s.includedQuantity !== 0)
              .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));

            return (
              <article key={plan.id} className={`plan${isCurrent ? ' current' : ''}`}>
                <div className="row">
                  <h2 style={{ fontSize: '1rem' }}>{plan.name}</h2>
                  {isCurrent && (
                    <span className="badge badge-success" style={{ marginLeft: 'auto' }}>
                      Current
                    </span>
                  )}
                </div>

                <div className="plan-price">
                  {plan.contactSalesOnly ? (
                    'Custom'
                  ) : (
                    <>
                      {formatMoney(plan.basePriceMinor, plan.currency)}
                      <span> / {plan.interval}</span>
                    </>
                  )}
                </div>
                <p className="small faint" style={{ marginTop: '-0.5rem' }}>
                  {plan.contactSalesOnly ? 'Priced individually' : 'Excludes GST'}
                </p>

                {plan.description && <p className="small muted">{plan.description}</p>}

                <ul className="plan-features">
                  {granted.map((service) => (
                    <li key={service.serviceKey}>
                      {describeGrant(service)}
                      {service.overagePriceMinor !== null && service.overagePriceMinor > 0 && (
                        <span className="faint">
                          , then{' '}
                          {formatRate(
                            service.overagePriceMinor,
                            service.overageUnitSize,
                            plan.currency,
                          )}{' '}
                          each
                        </span>
                      )}
                    </li>
                  ))}
                </ul>

                {plan.contactSalesOnly ? (
                  <a className="button button-secondary" href="mailto:sales@rioplay.in">
                    Contact sales
                  </a>
                ) : (
                  <button
                    type="button"
                    className="button-primary"
                    disabled={
                      !canManage ||
                      isCurrent ||
                      pending ||
                      // Blocked only while on a *paid* plan, where a change
                      // would have to prorate or schedule.
                      (current !== null && current.status !== 'canceled' && !onFreePlan)
                    }
                    onClick={() => void choose(plan)}
                  >
                    {pendingPlanId === plan.id && <Spinner />}
                    {isCurrent
                      ? 'Current plan'
                      : plan.basePriceMinor === 0
                        ? 'Switch to free'
                        : 'Choose plan'}
                  </button>
                )}
              </article>
            );
          })}
        </div>

        {!canManage && <p className="small muted">Only owners and admins can change the plan.</p>}

        <p className="small faint">
          Paid plans redirect to the payment provider to authorise a mandate. The subscription
          activates once the provider confirms the payment, which can take a moment after you
          return.
        </p>
      </div>
    </div>
  );
}
